/* ==========================================================================
   T3 — product rail arrows + AJAX quick add.
   The rail itself scrolls with native CSS scroll-snap; this file only drives
   the desktop arrow buttons and the round add-to-cart button.

   The class is declared INSIDE the customElements guard on purpose. A top-level
   `class T3Rail` threw "Identifier 'T3Rail' has already been declared" whenever
   the file was requested twice, and wrapping the file in an IIFE did not help:
   Shopify minifies theme assets and unwrapped the IIFE, putting the class back
   at the top level. A class inside an `if` block is block-scoped and survives
   being executed any number of times.
   ========================================================================== */

if (!customElements.get('t3-rail')) {
  class T3Rail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-t3-rail-track]');
    if (!this.track) return;

    this.prevBtn = this.querySelector('[data-t3-rail-prev]');
    this.nextBtn = this.querySelector('[data-t3-rail-next]');

    this.prevBtn?.addEventListener('click', () => this.page(-1));
    this.nextBtn?.addEventListener('click', () => this.page(1));

    this.onScroll = this.throttle(() => this.syncButtons(), 120);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    this.syncButtons();
    this.bindQuickAdd();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onScroll);
  }

  // Scroll by one card width * however many fit, so a click always lands on
  // a snap point instead of leaving a card half-cut.
  page(direction) {
    // Any rail child works — product cards here, review cards in the
    // testimonials section.
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const step = card.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor(this.track.clientWidth / step));
    this.track.scrollBy({
      left: step * perView * direction,
      behavior: this.reduceMotion() ? 'auto' : 'smooth',
    });
  }

  syncButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    this.prevBtn.disabled = this.track.scrollLeft <= 2;
    this.nextBtn.disabled = this.track.scrollLeft >= max;
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  throttle(fn, wait) {
    let last = 0;
    let queued = null;
    return () => {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn();
      } else if (!queued) {
        queued = setTimeout(() => {
          queued = null;
          last = Date.now();
          fn();
        }, wait);
      }
    };
  }

  /* ------------------------------------------------------------ quick add */

  bindQuickAdd() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-t3-add]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      this.addToCart(btn);
    });
  }

  async addToCart(btn) {
    const id = btn.dataset.t3Add;
    if (!id) return;

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const drawer = document.querySelector('cart-drawer');

    const body = { items: [{ id: Number(id), quantity: 1 }] };

    // Ask Shopify to re-render the drawer sections in the same round trip.
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      } catch (err) {
        /* drawer not ready — fall through without sections */
      }
    }

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');

      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      } else {
        this.bumpCartCount();
      }

      // PUB_SUB_EVENTS is a top-level `const` in constants.js, so it never lands
      // on window — the old `window.PUB_SUB_EVENTS` test was always false and
      // this event was never actually published. The header badge listens for it.
      if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 't3-rail', cartData: data });
      }
    } catch (err) {
      // Never leave the visitor stuck: fall back to the real cart page.
      console.error('[t3] add to cart failed', err);
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } finally {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  // Fallback for when there is no cart drawer (cart type = page/notification).
  // It used to look for Dawn's `.cart-count-bubble`, which this theme's header
  // does not render, so the badge never moved.
  bumpCartCount() {
    const badge = document.querySelector('[data-t3-cart-count]');
    if (!badge) return;
    const n = parseInt(badge.textContent, 10);
    if (Number.isNaN(n)) return;
    badge.textContent = String(n + 1);
    badge.classList.remove('is-empty');
  }
}

  customElements.define('t3-rail', T3Rail);
}
